import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  FileText, Download, Save, Plus, Trash2, Building2, User, 
  Calendar, Hash, DollarSign, ChevronLeft, Check, AlertCircle,
  Receipt, Clock, CreditCard, Package
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';

const CURRENCY_SYMBOLS = { ZAR: 'R', USD: '$', EUR: '€', GBP: '£' };

const LINE_ITEM_TYPES = [
  { value: 'stage', label: 'Stage/Milestone', icon: Package },
  { value: 'time', label: 'Time (Hours/Days)', icon: Clock },
  { value: 'disbursement', label: 'Disbursement', icon: Receipt },
  { value: 'payment', label: 'Less Payment', icon: CreditCard }
];

const formatCurrency = (amount, currency = 'ZAR') => {
  if (amount === null || amount === undefined) return '';
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  return `${symbol}${Math.abs(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ProjectInvoicePage = () => {
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const isEditing = !!invoiceId;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  
  // Data
  const [tenants, setTenants] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  
  // Invoice form state
  const [invoice, setInvoice] = useState({
    tenant_id: '',
    client_id: '',
    project_name: '',
    reference: '',
    vat_rate: 15,
    line_items: [],
    notes: '',
    status: 'draft'
  });
  
  // New client modal
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [newClient, setNewClient] = useState({
    legal_name: '',
    vat_number: '',
    registration_number: '',
    billing_address: {
      line1: '',
      line2: '',
      city: '',
      province: '',
      postal_code: '',
      country: 'South Africa'
    },
    contact_email: '',
    contact_person: '',
    payment_terms: 30
  });
  
  useEffect(() => {
    loadInitialData();
  }, [invoiceId]); // Re-run when invoiceId changes (after save navigates to edit URL)
  
  useEffect(() => {
    if (invoice.tenant_id) {
      loadClients(invoice.tenant_id);
    }
  }, [invoice.tenant_id]);
  
  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load tenants
      const tenantsRes = await api.getTenants({ limit: 100 });
      setTenants(tenantsRes.data.tenants || []);
      
      // Load existing invoice if editing (invoiceId exists in URL params)
      if (invoiceId) {
        const invoiceRes = await api.getInvoice(invoiceId);
        const inv = invoiceRes.data.invoice;
        setInvoice({
          tenant_id: inv.tenant_id,
          client_id: inv.client_id || '',
          project_name: inv.project_name || '',
          reference: inv.reference || '',
          vat_rate: inv.vat_rate !== undefined ? inv.vat_rate : 15,
          line_items: inv.line_items || [],
          notes: inv.notes || '',
          status: inv.status || 'draft',
          invoice_number: inv.invoice_number || ''
        });
        
        // Set tenant for billing info
        const tenant = tenantsRes.data.tenants.find(t => t.id === inv.tenant_id);
        if (tenant) setSelectedTenant(tenant);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const loadClients = async (tenantId) => {
    try {
      const res = await api.getClients({ tenant_id: tenantId });
      setClients(res.data.clients || []);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };
  
  const handleTenantChange = (tenantId) => {
    const tenant = tenants.find(t => t.id === tenantId);
    setSelectedTenant(tenant);
    setInvoice(prev => ({ 
      ...prev, 
      tenant_id: tenantId,
      client_id: '' // Reset client when tenant changes
    }));
  };
  
  const addLineItem = () => {
    setInvoice(prev => ({
      ...prev,
      line_items: [
        ...prev.line_items,
        {
          type: 'stage',
          description: '',
          quantity: 1,
          unit_price: 0,
          subtotal: 0
        }
      ]
    }));
  };
  
  const updateLineItem = (index, field, value) => {
    setInvoice(prev => {
      const items = [...prev.line_items];
      items[index] = { ...items[index], [field]: value };
      
      // Recalculate subtotal
      const qty = items[index].quantity || 0;
      const rate = items[index].unit_price || 0;
      items[index].subtotal = qty * rate;
      
      return { ...prev, line_items: items };
    });
  };
  
  const removeLineItem = (index) => {
    setInvoice(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };
  
  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    invoice.line_items.forEach(item => {
      const amount = (item.quantity || 0) * (item.unit_price || 0);
      if (item.type === 'payment') {
        subtotal -= Math.abs(amount);
      } else {
        subtotal += amount;
      }
    });
    
    const vatAmount = subtotal > 0 ? subtotal * (invoice.vat_rate / 100) : 0;
    const total = subtotal + vatAmount;
    
    return { subtotal, vatAmount, total };
  };
  
  const { subtotal, vatAmount, total } = calculateTotals();
  
  const handleSave = async (status = 'draft') => {
    if (!invoice.tenant_id) {
      toast.error('Please select a tenant');
      return;
    }
    
    if (invoice.line_items.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }
    
    try {
      setSaving(true);
      
      const payload = {
        ...invoice,
        status
      };
      
      let result;
      if (isEditing) {
        result = await api.updateProjectInvoice(invoiceId, payload);
      } else {
        result = await api.createProjectInvoice(payload);
      }
      
      toast.success(isEditing ? 'Invoice updated' : 'Invoice created');
      
      if (!isEditing && result.data.invoice_id) {
        navigate(`/billing/invoice/${result.data.invoice_id}`);
      }
    } catch (error) {
      console.error('Failed to save invoice:', error);
      toast.error('Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDownloadPdf = async () => {
    if (!isEditing) {
      toast.error('Please save the invoice first');
      return;
    }
    
    try {
      setDownloading(true);
      
      const response = await api.downloadInvoicePdf(invoiceId);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoice.invoice_number || 'invoice'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };
  
  const handleCreateClient = async () => {
    if (!newClient.legal_name) {
      toast.error('Client name is required');
      return;
    }
    
    try {
      const result = await api.createClient({
        ...newClient,
        tenant_id: invoice.tenant_id
      });
      
      toast.success('Client created');
      setShowNewClientModal(false);
      
      // Reload clients and select the new one
      await loadClients(invoice.tenant_id);
      setInvoice(prev => ({ ...prev, client_id: result.data.client_id }));
      
      // Reset form
      setNewClient({
        legal_name: '',
        vat_number: '',
        registration_number: '',
        billing_address: {
          line1: '',
          line2: '',
          city: '',
          province: '',
          postal_code: '',
          country: 'South Africa'
        },
        contact_email: '',
        contact_person: '',
        payment_terms: 30
      });
    } catch (error) {
      console.error('Failed to create client:', error);
      toast.error('Failed to create client');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--clr-surface-subtle)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--clr-brand-primary)]"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[var(--clr-surface-subtle)]">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg"
                data-testid="back-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {isEditing ? `Edit Invoice ${invoice.invoice_number || ''}` : 'Create Project Invoice'}
                </h1>
                <p className="text-sm text-gray-500">Professional invoice generation</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {isEditing && invoice.status === 'draft' && (
                <button
                  onClick={handleDownloadPdf}
                  disabled={downloading}
                  className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4" />
                  {downloading ? 'Generating...' : 'Download PDF'}
                </button>
              )}
              
              <button
                onClick={() => handleSave('draft')}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                data-testid="save-draft-btn"
              >
                <Save className="w-4 h-4" />
                Save Draft
              </button>
              
              <button
                onClick={() => handleSave('issued')}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                data-testid="issue-invoice-btn"
              >
                <Check className="w-4 h-4" />
                Issue Invoice
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-3 gap-6">
          {/* Left Column - Invoice Details */}
          <div className="col-span-2 space-y-6">
            {/* Client Selection */}
            <div className="bg-white rounded-xl border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Client Details
              </h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tenant *</label>
                  <select
                    value={invoice.tenant_id}
                    onChange={(e) => handleTenantChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="tenant-select"
                  >
                    <option value="">Select tenant...</option>
                    {tenants.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                  <div className="flex gap-2">
                    <select
                      value={invoice.client_id}
                      onChange={(e) => setInvoice(prev => ({ ...prev, client_id: e.target.value }))}
                      disabled={!invoice.tenant_id}
                      className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                      data-testid="client-select"
                    >
                      <option value="">Select client (optional)...</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>{c.legal_name}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => setShowNewClientModal(true)}
                      disabled={!invoice.tenant_id}
                      className="px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      data-testid="new-client-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
                  <input
                    type="text"
                    value={invoice.project_name}
                    onChange={(e) => setInvoice(prev => ({ ...prev, project_name: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Digital Transformation Phase 1"
                    data-testid="project-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                  <input
                    type="text"
                    value={invoice.reference}
                    onChange={(e) => setInvoice(prev => ({ ...prev, reference: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., PO-12345"
                    data-testid="reference-input"
                  />
                </div>
              </div>
            </div>
            
            {/* Line Items */}
            <div className="bg-white rounded-xl border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Line Items
                </h2>
                <button
                  onClick={addLineItem}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm"
                  data-testid="add-line-item-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Item
                </button>
              </div>
              
              {invoice.line_items.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed rounded-lg">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No line items yet</p>
                  <button
                    onClick={addLineItem}
                    className="mt-3 text-blue-600 hover:underline text-sm"
                  >
                    Add your first item
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Header */}
                  <div className="grid grid-cols-12 gap-3 text-xs font-medium text-gray-500 uppercase px-2">
                    <div className="col-span-2">Type</div>
                    <div className="col-span-4">Description</div>
                    <div className="col-span-1 text-right">Qty</div>
                    <div className="col-span-2 text-right">Rate</div>
                    <div className="col-span-2 text-right">Amount</div>
                    <div className="col-span-1"></div>
                  </div>
                  
                  {invoice.line_items.map((item, index) => (
                    <div 
                      key={index} 
                      className={`grid grid-cols-12 gap-3 items-center p-3 rounded-lg ${
                        item.type === 'payment' ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                      }`}
                      data-testid={`line-item-${index}`}
                    >
                      <div className="col-span-2">
                        <select
                          value={item.type}
                          onChange={(e) => updateLineItem(index, 'type', e.target.value)}
                          className="w-full px-2 py-1.5 border rounded text-sm"
                        >
                          {LINE_ITEM_TYPES.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-4">
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                          className="w-full px-2 py-1.5 border rounded text-sm"
                          placeholder="Description"
                        />
                      </div>
                      <div className="col-span-1">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1.5 border rounded text-sm text-right"
                          min="0"
                          step="0.5"
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1.5 border rounded text-sm text-right"
                          min="0"
                        />
                      </div>
                      <div className="col-span-2 text-right font-medium">
                        {item.type === 'payment' ? (
                          <span className="text-green-600">
                            -{formatCurrency((item.quantity || 0) * (item.unit_price || 0), 'ZAR')}
                          </span>
                        ) : (
                          formatCurrency((item.quantity || 0) * (item.unit_price || 0), 'ZAR')
                        )}
                      </div>
                      <div className="col-span-1 text-right">
                        <button
                          onClick={() => removeLineItem(index)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Notes */}
            <div className="bg-white rounded-xl border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
              <textarea
                value={invoice.notes}
                onChange={(e) => setInvoice(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Additional notes for the invoice..."
                data-testid="notes-input"
              />
            </div>
          </div>
          
          {/* Right Column - Summary */}
          <div className="space-y-6">
            {/* Totals Card */}
            <div className="bg-white rounded-xl border p-6 sticky top-24">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-blue-600" />
                Invoice Summary
              </h2>
              
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="font-medium">{formatCurrency(subtotal, 'ZAR')}</span>
                </div>
                
                <div className="flex justify-between items-center text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">VAT</span>
                    <input
                      type="number"
                      value={invoice.vat_rate}
                      onChange={(e) => setInvoice(prev => ({ ...prev, vat_rate: parseFloat(e.target.value) || 0 }))}
                      className="w-16 px-2 py-1 border rounded text-right text-xs"
                      min="0"
                      max="100"
                      data-testid="vat-rate-input"
                    />
                    <span className="text-gray-500">%</span>
                  </div>
                  <span className="font-medium">{formatCurrency(vatAmount, 'ZAR')}</span>
                </div>
                
                <div className="pt-3 border-t">
                  <div className="flex justify-between">
                    <span className="text-lg font-bold text-gray-900">Total Due</span>
                    <span className="text-xl font-bold text-blue-600">{formatCurrency(total, 'ZAR')}</span>
                  </div>
                </div>
              </div>
              
              {/* Status Badge */}
              {isEditing && (
                <div className="mt-6 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Status</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                      invoice.status === 'issued' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {invoice.status?.toUpperCase() || 'DRAFT'}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Quick Actions */}
              {isEditing && (
                <div className="mt-6 pt-4 border-t space-y-2">
                  <button
                    onClick={handleDownloadPdf}
                    disabled={downloading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    data-testid="download-invoice-btn"
                  >
                    <Download className="w-5 h-5" />
                    {downloading ? 'Generating PDF...' : 'Download Invoice'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* New Client Modal */}
      {showNewClientModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowNewClientModal(false)}></div>
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg p-6 z-10 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Create New Client
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Legal Name *</label>
                <input
                  type="text"
                  value={newClient.legal_name}
                  onChange={(e) => setNewClient(prev => ({ ...prev, legal_name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Company Name (Pty) Ltd"
                  data-testid="client-legal-name"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">VAT Number</label>
                  <input
                    type="text"
                    value={newClient.vat_number}
                    onChange={(e) => setNewClient(prev => ({ ...prev, vat_number: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="4123456789"
                    data-testid="client-vat"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Registration No.</label>
                  <input
                    type="text"
                    value={newClient.registration_number}
                    onChange={(e) => setNewClient(prev => ({ ...prev, registration_number: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="2020/123456/07"
                    data-testid="client-reg"
                  />
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-900 mb-3">Billing Address</h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={newClient.billing_address.line1}
                    onChange={(e) => setNewClient(prev => ({ 
                      ...prev, 
                      billing_address: { ...prev.billing_address, line1: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Street Address"
                    data-testid="client-address-line1"
                  />
                  <input
                    type="text"
                    value={newClient.billing_address.line2}
                    onChange={(e) => setNewClient(prev => ({ 
                      ...prev, 
                      billing_address: { ...prev.billing_address, line2: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Building/Suite (optional)"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={newClient.billing_address.city}
                      onChange={(e) => setNewClient(prev => ({ 
                        ...prev, 
                        billing_address: { ...prev.billing_address, city: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="City"
                      data-testid="client-city"
                    />
                    <input
                      type="text"
                      value={newClient.billing_address.province}
                      onChange={(e) => setNewClient(prev => ({ 
                        ...prev, 
                        billing_address: { ...prev.billing_address, province: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Province"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={newClient.billing_address.postal_code}
                      onChange={(e) => setNewClient(prev => ({ 
                        ...prev, 
                        billing_address: { ...prev.billing_address, postal_code: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Postal Code"
                      data-testid="client-postal"
                    />
                    <input
                      type="text"
                      value={newClient.billing_address.country}
                      onChange={(e) => setNewClient(prev => ({ 
                        ...prev, 
                        billing_address: { ...prev.billing_address, country: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Country"
                    />
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-900 mb-3">Contact Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                    <input
                      type="email"
                      value={newClient.contact_email}
                      onChange={(e) => setNewClient(prev => ({ ...prev, contact_email: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="accounts@company.com"
                      data-testid="client-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                    <input
                      type="text"
                      value={newClient.contact_person}
                      onChange={(e) => setNewClient(prev => ({ ...prev, contact_person: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="John Smith"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms (days)</label>
                <input
                  type="number"
                  value={newClient.payment_terms}
                  onChange={(e) => setNewClient(prev => ({ ...prev, payment_terms: parseInt(e.target.value) || 30 }))}
                  className="w-full px-3 py-2 border rounded-lg"
                  min="0"
                  data-testid="client-payment-terms"
                />
              </div>
            </div>
            
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowNewClientModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateClient}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                data-testid="create-client-submit"
              >
                Create Client
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectInvoicePage;
